import React, { useState } from "react";
import PageHeader from "../components/PageHeader";
import { useTasks, useTeam, useLeads } from "../../hooks/useFirebase";
import { TasksService } from "../../firebase/databaseService";

const TasksPage = () => {
  const { tasks, loading, updateTask, deleteTask } = useTasks();
  const { teamMembers } = useTeam();
  const { leads } = useLeads();

  // ========== MODAL STATES ==========
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showItemEditModal, setShowItemEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");

  // ========== FILTERS ==========
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterMember, setFilterMember] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [detailFilter, setDetailFilter] = useState("all");

  // ========== ASSIGN FORM ==========
  const [assignForm, setAssignForm] = useState({
    title: "", taskType: "calling", assignedTo: "", assignedName: "",
    priority: "medium", dueDate: "", description: "", selectedLeadIds: [],
  });

  // ========== ITEM EDIT FORM ==========
  const [itemEditForm, setItemEditForm] = useState({
    callDate: "", decisionMaker: "", requirement: "",
    painPoint: "", employeeNotes: "", status: "completed",
  });

  // ========== NOTE FORM ==========
  const [noteInput, setNoteInput] = useState("");

  // ========== HANDLERS ==========
  const handleAssignChange = (e) => {
    const { name, value } = e.target;
    setAssignForm((prev) => ({ ...prev, [name]: value }));
    if (name === "assignedTo") {
      const member = teamMembers.find((m) => m.id === value);
      if (member) setAssignForm((prev) => ({ ...prev, assignedTo: value, assignedName: member.name, assignedRole: member.role }));
    }
  };

  const toggleLeadSelection = (leadId) => {
    setAssignForm((prev) => {
      const isSelected = prev.selectedLeadIds.includes(leadId);
      return { ...prev, selectedLeadIds: isSelected ? prev.selectedLeadIds.filter((id) => id !== leadId) : [...prev.selectedLeadIds, leadId] };
    });
  };

  const handleAssignTask = async () => {
    if (!assignForm.title) { setModalError("Title required!"); return; }
    if (!assignForm.assignedTo) { setModalError("Member select karo!"); return; }
    if (!assignForm.dueDate) { setModalError("Due date select karo!"); return; }
    if (assignForm.selectedLeadIds.length === 0) { setModalError("Kam se kam ek lead select karo!"); return; }

    setModalLoading(true);
    setModalError("");

    try {
      const assignedItems = assignForm.selectedLeadIds.map((leadId) => {
        const lead = leads.find((l) => l.id === leadId);
        return {
          id: leadId, leadId: lead?.leadId || "",
          businessName: lead?.businessName || lead?.name || "Unknown",
          category: lead?.category || lead?.source || "",
          contactNumber: lead?.phone || lead?.contactNumber || "",
          name: lead?.name || lead?.businessName || "Unknown",
          phone: lead?.phone || lead?.contactNumber || "",
          callDate: "", decisionMaker: "", requirement: "",
          painPoint: "", employeeNotes: "", status: "pending",
          updatedBy: "", updatedAt: null,
        };
      });

      await TasksService.createTask({
        title: assignForm.title, taskType: assignForm.taskType,
        assignedTo: assignForm.assignedTo, assignedName: assignForm.assignedName,
        assignedRole: assignForm.assignedRole || "", priority: assignForm.priority,
        dueDate: assignForm.dueDate, description: assignForm.description,
        assignedItems, totalItems: assignedItems.length, completedItems: [],
        progress: 0, status: "pending", createdBy: "admin",
      });

      setModalSuccess(`✅ ${assignedItems.length} leads assigned!`);
      setAssignForm({ title: "", taskType: "calling", assignedTo: "", assignedName: "", priority: "medium", dueDate: "", description: "", selectedLeadIds: [] });
      setTimeout(() => { setShowAssignModal(false); setModalSuccess(""); }, 2000);
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const openDetailModal = (task) => {
    setSelectedTask(task);
    setDetailFilter("all");
    setShowDetailModal(true);
  };

  const openItemEdit = (task, item) => {
    setSelectedTask(task);
    setSelectedItem(item);
    setItemEditForm({
      callDate: item.callDate || "", decisionMaker: item.decisionMaker || "",
      requirement: item.requirement || "", painPoint: item.painPoint || "",
      employeeNotes: item.employeeNotes || "", status: item.status || "pending",
    });
    setModalError("");
    setModalSuccess("");
    setShowItemEditModal(true);
  };

  const handleItemEditChange = (e) => {
    setItemEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveItemEdit = async () => {
    if (!selectedTask || !selectedItem) return;
    setModalLoading(true);
    setModalError("");
    try {
      const updates = {
        callDate: itemEditForm.callDate, decisionMaker: itemEditForm.decisionMaker,
        requirement: itemEditForm.requirement, painPoint: itemEditForm.painPoint,
        employeeNotes: itemEditForm.employeeNotes, status: itemEditForm.status,
        updatedBy: "Admin",
      };
      if (itemEditForm.status === "completed") updates.completedAt = new Date().toISOString();
      await TasksService.updateTaskItem(selectedTask.id, selectedItem.id, updates);
      const allTasks = await TasksService.getAllTasks();
      const updated = allTasks.find((t) => t.id === selectedTask.id);
      if (updated) setSelectedTask(updated);
      setModalSuccess("✅ Saved!");
      setTimeout(() => { setShowItemEditModal(false); setModalSuccess(""); }, 1500);
    } catch (error) {
      setModalError("Failed: " + error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleQuickStatus = async (task, itemId, status) => {
    setModalLoading(true);
    try {
      await TasksService.updateTaskItem(task.id, itemId, { status, completedAt: status === "completed" ? new Date().toISOString() : null });
      const allTasks = await TasksService.getAllTasks();
      const updated = allTasks.find((t) => t.id === task.id);
      if (updated) setSelectedTask(updated);
    } catch (error) {
      setModalError(error.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleMarkAllCompleted = async (task) => {
    setModalLoading(true);
    try {
      const items = (task.assignedItems || []).filter((i) => i.status !== "completed");
      for (const item of items) {
        await TasksService.updateTaskItem(task.id, item.id, { status: "completed", completedAt: new Date().toISOString() });
      }
      const allTasks = await TasksService.getAllTasks();
      const updated = allTasks.find((t) => t.id === task.id);
      if (updated) setSelectedTask(updated);
      setModalSuccess("✅ All completed!");
      setTimeout(() => setModalSuccess(""), 1500);
    } catch (error) { setModalError(error.message); } finally { setModalLoading(false); }
  };

  const handleAddNote = async () => {
    if (!noteInput.trim() || !selectedTask) return;
    setModalLoading(true);
    try {
      await TasksService.addNoteToTask(selectedTask.id, noteInput.trim(), "admin", "Admin");
      const allTasks = await TasksService.getAllTasks();
      const updated = allTasks.find((t) => t.id === selectedTask.id);
      if (updated) setSelectedTask(updated);
      setNoteInput("");
    } catch (error) { setModalError(error.message); } finally { setModalLoading(false); }
  };

  const openDeleteModal = (task) => { setTaskToDelete(task); setShowDeleteModal(true); };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    setModalLoading(true);
    try { await deleteTask(taskToDelete.id); setTaskToDelete(null); setShowDeleteModal(false); }
    catch (error) { setModalError(error.message); } finally { setModalLoading(false); }
  };

  // ========== FILTERED & STATS ==========
  const filteredTasks = tasks.filter((task) => {
    const matchesSearch = task.title?.toLowerCase().includes(searchTerm.toLowerCase()) || task.assignedName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || task.status === filterStatus;
    const matchesMember = filterMember === "all" || task.assignedTo === filterMember;
    return matchesSearch && matchesStatus && matchesMember;
  });

  const getTeamPerformance = () => {
    const memberMap = {};
    teamMembers.forEach((m) => { memberMap[m.id] = { name: m.name, role: m.role, totalTasks: 0, completedTasks: 0, inProgressTasks: 0, totalItems: 0, completedItems: 0, overdueTasks: 0 }; });
    tasks.forEach((task) => {
      const member = memberMap[task.assignedTo];
      if (member) {
        member.totalTasks++;
        if (task.status === "completed") member.completedTasks++;
        else if (task.status === "in_progress") member.inProgressTasks++;
        member.totalItems += task.totalItems || task.assignedItems?.length || 0;
        member.completedItems += (task.assignedItems || []).filter((i) => i.status === "completed").length;
        if (task.status !== "completed" && task.dueDate < new Date().toISOString().split("T")[0]) member.overdueTasks++;
      }
    });
    return Object.values(memberMap);
  };

  const teamPerformance = getTeamPerformance();

  // Detail modal filtered items
  const detailItems = selectedTask ? (selectedTask.assignedItems || []).filter((item) => {
    if (detailFilter === "all") return true;
    return item.status === detailFilter;
  }) : [];

  const detailStats = selectedTask ? {
    total: (selectedTask.assignedItems || []).length,
    completed: (selectedTask.assignedItems || []).filter((i) => i.status === "completed").length,
    inProgress: (selectedTask.assignedItems || []).filter((i) => i.status === "in_progress").length,
    pending: (selectedTask.assignedItems || []).filter((i) => i.status === "pending").length,
  } : { total: 0, completed: 0, inProgress: 0, pending: 0 };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div>
      <PageHeader title="Task Management" breadcrumbs={["Admin", "Tasks"]} />

      {/* Stats */}
      <div className="admin-grid admin-grid-5" style={{ marginTop: 24, marginBottom: 24 }}>
        {[
          { label: "Total Tasks", value: tasks.length, icon: "📋", cls: "qs-blue" },
          { label: "Pending", value: tasks.filter((t) => t.status === "pending").length, icon: "⏳", cls: "qs-orange" },
          { label: "In Progress", value: tasks.filter((t) => t.status === "in_progress").length, icon: "🔄", cls: "qs-orange" },
          { label: "Completed", value: tasks.filter((t) => t.status === "completed").length, icon: "✅", cls: "qs-green" },
          { label: "Overdue", value: tasks.filter((t) => t.status !== "completed" && t.dueDate < new Date().toISOString().split("T")[0]).length, icon: "⚠️", cls: "qs-red" },
        ].map((s, i) => (
          <div key={i} className={`quick-stat ${s.cls}`}>
            <div className="quick-stat-icon">{s.icon}</div>
            <div className="quick-stat-info">
              <div className="quick-stat-label">{s.label}</div>
              <div className="quick-stat-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar" style={{ marginBottom: 24 }}>
        <div className="toolbar-left">
          <div className="admin-form-group" style={{ minWidth: 200 }}>
            <input type="text" placeholder="🔍 Search task, member..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <div className="admin-form-group">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
            </select>
          </div>
          <div className="admin-form-group">
            <select value={filterMember} onChange={(e) => setFilterMember(e.target.value)}>
              <option value="all">All Members</option>
              {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
        </div>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={() => { setModalError(""); setModalSuccess(""); setShowAssignModal(true); }}>+ Assign Task</button>
        </div>
      </div>

      {/* Team Performance */}
      <div className="data-table-wrapper" style={{ marginBottom: 24 }}>
        <div className="data-table-toolbar"><div className="toolbar-title"><h3>🏆 Team Performance</h3></div></div>
        <div className="data-table-scroll">
          <table className="data-table">
            <thead><tr><th>Member</th><th>Role</th><th>Tasks</th><th>Done</th><th>Items</th><th>Progress</th><th>Overdue</th></tr></thead>
            <tbody>
              {teamPerformance.map((m, i) => {
                const prog = m.totalItems > 0 ? Math.round((m.completedItems / m.totalItems) * 100) : 0;
                return (
                  <tr key={i}>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><div className="admin-avatar small"><span>{m.name?.split(" ").map((n) => n[0]).join("")}</span></div><span className="font-bold text-14">{m.name}</span></div></td>
                    <td><span className={`status-badge badge-${m.role}`}>{m.role}</span></td>
                    <td className="font-semibold">{m.totalTasks}</td>
                    <td className="font-bold" style={{ color: "#16a34a" }}>{m.completedTasks}</td>
                    <td className="font-semibold">{m.completedItems}/{m.totalItems}</td>
                    <td><div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 60, height: 5, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}><div style={{ width: `${prog}%`, height: "100%", backgroundColor: prog >= 80 ? "#22c55e" : prog >= 40 ? "#3b82f6" : "#f97316", borderRadius: 9999 }} /></div><span className="text-12 font-bold">{prog}%</span></div></td>
                    <td>{m.overdueTasks > 0 ? <span className="status-badge badge-overdue">{m.overdueTasks}</span> : <span className="status-badge badge-completed">0</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="admin-loading"><div className="spinner-wrap"><div className="spinner" /><p className="spinner-text">Loading...</p></div></div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center text-gray-400" style={{ backgroundColor: "white", borderRadius: 12, padding: 48 }}><p className="text-24 mb-2">📭</p><p>No tasks found.</p></div>
      ) : (
        <div className="task-list">
          {filteredTasks.map((task) => {
            const isOverdue = task.status !== "completed" && task.dueDate < new Date().toISOString().split("T")[0];
            const completed = (task.assignedItems || []).filter((i) => i.status === "completed").length;
            const total = task.totalItems || task.assignedItems?.length || 0;
            const prog = task.progress || (total > 0 ? Math.round((completed / total) * 100) : 0);
            return (
              <div key={task.id} className={`task-card ${task.status === "completed" ? "task-completed" : isOverdue ? "task-overdue" : task.priority === "high" ? "task-high" : "task-medium"}`}>
                <div className="task-card-inner" style={{ flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%" }}>
                    <div>
                      <h4 className="task-title">{task.title}</h4>
                      <div className="task-meta" style={{ marginTop: 6 }}>
                        <span>👤 {task.assignedName}</span><span>🏷 {task.taskType?.replace("_", " ")}</span>
                        <span className={isOverdue ? "overdue" : ""}>📅 {task.dueDate}{isOverdue ? " (Overdue)" : ""}</span>
                        <span>📦 {completed}/{total} done</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <span className={`status-badge badge-priority-${task.priority}`}>{task.priority}</span>
                      <span className={`status-badge badge-${task.status}`}>{task.status.replace("_", " ")}</span>
                    </div>
                  </div>
                  <div style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span className="text-12 text-gray-500">Progress</span><span className="text-12 font-bold" style={{ color: prog === 100 ? "#16a34a" : "#3b82f6" }}>{prog}%</span></div>
                    <div style={{ width: "100%", height: 8, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}><div style={{ width: `${prog}%`, height: "100%", backgroundColor: prog === 100 ? "#22c55e" : prog >= 50 ? "#3b82f6" : "#f97316", borderRadius: 9999 }} /></div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, width: "100%" }}>
                    <button className="btn btn-ghost-blue" onClick={() => openDetailModal(task)}>👁 View Details ({total})</button>
                    <button className="btn btn-ghost-red" onClick={() => openDeleteModal(task)}>🗑 Delete</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ==========================================
          ASSIGN TASK MODAL
      ========================================== */}
      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-box modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <div className="modal-header"><h3>📋 Assign Task</h3><button className="modal-close-btn" onClick={() => setShowAssignModal(false)}>✕</button></div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              <div className="admin-form-group" style={{ marginBottom: 16 }}><label>Task Title *</label><input type="text" name="title" value={assignForm.title} onChange={handleAssignChange} placeholder="e.g. Calling Batch - IT Companies" disabled={modalLoading} /></div>
              <div className="form-row">
                <div className="admin-form-group"><label>Task Type *</label><select name="taskType" value={assignForm.taskType} onChange={handleAssignChange} disabled={modalLoading}><option value="calling">📞 Calling</option><option value="data_entry">📝 Data Entry</option><option value="proposal">📄 Proposal</option><option value="demo">🖥 Demo</option><option value="follow_up">🔄 Follow-up</option></select></div>
                <div className="admin-form-group"><label>Assign To *</label><select name="assignedTo" value={assignForm.assignedTo} onChange={handleAssignChange} disabled={modalLoading}><option value="">Select Member</option>{teamMembers.filter((m) => m.isActive !== false).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}</select></div>
              </div>
              <div className="form-row">
                <div className="admin-form-group"><label>Priority</label><select name="priority" value={assignForm.priority} onChange={handleAssignChange} disabled={modalLoading}><option value="low">🟢 Low</option><option value="medium">🟡 Medium</option><option value="high">🔴 High</option></select></div>
                <div className="admin-form-group"><label>Due Date *</label><input type="date" name="dueDate" value={assignForm.dueDate} onChange={handleAssignChange} disabled={modalLoading} /></div>
              </div>
              <div className="admin-form-group" style={{ marginBottom: 16 }}><label>Description</label><textarea name="description" value={assignForm.description} onChange={handleAssignChange} rows="2" placeholder="Instructions..." disabled={modalLoading} /></div>
              <div style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: 0 }}>📦 Select Leads ({assignForm.selectedLeadIds.length})</h4>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-ghost-blue" onClick={() => setAssignForm((p) => ({ ...p, selectedLeadIds: leads.map((l) => l.id) }))} disabled={modalLoading}>Select All</button>
                    <button className="btn btn-ghost-red" onClick={() => setAssignForm((p) => ({ ...p, selectedLeadIds: [] }))} disabled={modalLoading}>Clear</button>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  {[5, 10, 20, 50].map((count) => (<button key={count} className="btn btn-outline" style={{ fontSize: 12, padding: "4px 12px" }} onClick={() => setAssignForm((p) => ({ ...p, selectedLeadIds: leads.slice(0, count).map((l) => l.id) }))} disabled={modalLoading}>First {count}</button>))}
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, padding: 8 }}>
                  {leads.length === 0 ? <p style={{ textAlign: "center", color: "#9ca3af", fontSize: 13, padding: 20 }}>No leads. Upload from Bulk Upload.</p> : leads.map((lead) => {
                    const isSelected = assignForm.selectedLeadIds.includes(lead.id);
                    return (
                      <div key={lead.id} onClick={() => toggleLeadSelection(lead.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 8, cursor: "pointer", backgroundColor: isSelected ? "#eff6ff" : "transparent", border: isSelected ? "1px solid #93c5fd" : "1px solid transparent", marginBottom: 4 }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleLeadSelection(lead.id)} style={{ accentColor: "#2563eb" }} />
                        <div className="admin-avatar small"><span>{(lead.name || lead.businessName || "?").split(" ").map((n) => n[0]).join("").substring(0, 2)}</span></div>
                        <div style={{ flex: 1 }}><div className="font-bold text-14">{lead.businessName || lead.name}</div><div className="text-12 text-gray-400">{lead.phone || lead.contactNumber} • {lead.category || lead.source}</div></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAssignModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAssignTask} disabled={modalLoading}>{modalLoading ? "⏳" : `📋 Assign ${assignForm.selectedLeadIds.length} Items`}</button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          ✨ IMPROVED DETAIL MODAL
      ========================================== */}
      {showDetailModal && selectedTask && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)} style={{ alignItems: "flex-start", paddingTop: 40, overflow: "auto" }}>
          <div
            className="modal-box"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 960, width: "95%", maxHeight: "90vh", overflowY: "auto", padding: 0 }}
          >
            {/* ========== HEADER ========== */}
            <div style={{
              padding: "24px 28px",
              borderBottom: "1px solid #e5e7eb",
              background: "linear-gradient(135deg, #f0f9ff, #ffffff)",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <span className={`status-badge badge-${selectedTask.status}`} style={{ fontSize: 12, padding: "4px 14px" }}>
                      {selectedTask.status.replace("_", " ").toUpperCase()}
                    </span>
                    <span className={`status-badge badge-priority-${selectedTask.priority}`} style={{ fontSize: 12, padding: "4px 14px" }}>
                      {selectedTask.priority.toUpperCase()} PRIORITY
                    </span>
                    <span style={{ fontSize: 12, color: "#6b7280" }}>🏷 {selectedTask.taskType?.replace("_", " ")}</span>
                  </div>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 8px" }}>
                    {selectedTask.title}
                  </h2>
                  <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13, color: "#6b7280" }}>
                    <span>👤 <strong>{selectedTask.assignedName}</strong></span>
                    <span>📅 Due: <strong style={{ color: selectedTask.dueDate < new Date().toISOString().split("T")[0] ? "#dc2626" : "#374151" }}>{selectedTask.dueDate}</strong></span>
                    <span>📦 <strong>{detailStats.total}</strong> items</span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid #e5e7eb", background: "white", cursor: "pointer", fontSize: 18, color: "#6b7280", flexShrink: 0 }}
                >✕</button>
              </div>

              {selectedTask.description && (
                <div style={{ marginTop: 12, padding: "10px 16px", backgroundColor: "#eff6ff", borderRadius: 8, border: "1px solid #bfdbfe", fontSize: 13, color: "#1e40af" }}>
                  📋 {selectedTask.description}
                </div>
              )}
            </div>

            {/* ========== PROGRESS SECTION ========== */}
            <div style={{ padding: "20px 28px", borderBottom: "1px solid #f3f4f6" }}>
              {/* Stats Cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 16 }}>
                {[
                  { label: "Total", value: detailStats.total, color: "#2563eb", bg: "#eff6ff" },
                  { label: "Completed", value: detailStats.completed, color: "#16a34a", bg: "#f0fdf4" },
                  { label: "In Progress", value: detailStats.inProgress, color: "#ea580c", bg: "#fff7ed" },
                  { label: "Pending", value: detailStats.pending, color: "#dc2626", bg: "#fef2f2" },
                ].map((s, i) => (
                  <div key={i} style={{
                    padding: "14px 16px",
                    backgroundColor: s.bg,
                    borderRadius: 10,
                    textAlign: "center",
                    border: `1px solid ${s.color}20`
                  }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Progress Bar */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>Overall Progress</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: selectedTask.progress === 100 ? "#16a34a" : "#2563eb" }}>
                    {selectedTask.progress || 0}%
                  </span>
                </div>
                <div style={{ width: "100%", height: 10, backgroundColor: "#f3f4f6", borderRadius: 9999, overflow: "hidden" }}>
                  <div style={{
                    width: `${selectedTask.progress || 0}%`,
                    height: "100%",
                    background: selectedTask.progress === 100 ? "linear-gradient(90deg, #22c55e, #16a34a)" : "linear-gradient(90deg, #3b82f6, #2563eb)",
                    borderRadius: 9999,
                    transition: "width 0.7s ease"
                  }} />
                </div>
              </div>

              {/* Quick Actions */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn-success" style={{ fontSize: 12 }} onClick={() => handleMarkAllCompleted(selectedTask)} disabled={modalLoading}>
                  ✅ Mark All Completed
                </button>
                <span style={{ fontSize: 12, color: "#9ca3af", alignSelf: "center" }}>
                  Last updated: {selectedTask.updatedAt ? new Date(selectedTask.updatedAt).toLocaleString("en-IN") : "—"}
                </span>
              </div>
            </div>

            {/* ========== FILTER TABS ========== */}
            <div style={{ padding: "16px 28px", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { id: "all", label: "All", count: detailStats.total, color: "#6b7280" },
                  { id: "completed", label: "✅ Done", count: detailStats.completed, color: "#16a34a" },
                  { id: "in_progress", label: "🔄 In Progress", count: detailStats.inProgress, color: "#ea580c" },
                  { id: "pending", label: "⏳ Pending", count: detailStats.pending, color: "#dc2626" },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setDetailFilter(f.id)}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 9999,
                      border: detailFilter === f.id ? `2px solid ${f.color}` : "1px solid #e5e7eb",
                      backgroundColor: detailFilter === f.id ? `${f.color}15` : "white",
                      color: detailFilter === f.id ? f.color : "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
              <span style={{ fontSize: 12, color: "#9ca3af" }}>Showing {detailItems.length} of {detailStats.total}</span>
            </div>

            {/* ========== ITEMS LIST ========== */}
            <div style={{ padding: "20px 28px", maxHeight: 500, overflowY: "auto" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {detailItems.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>
                    <p style={{ fontSize: 40, margin: 0 }}>📭</p>
                    <p style={{ fontSize: 14, margin: "8px 0 0" }}>No items in this filter</p>
                  </div>
                ) : (
                  detailItems.map((item, idx) => {
                    const itemStatusColor = item.status === "completed" ? "#22c55e" : item.status === "in_progress" ? "#f97316" : "#9ca3af";
                    const itemBg = item.status === "completed" ? "#f0fdf4" : item.status === "in_progress" ? "#fff7ed" : "#f9fafb";

                    return (
                      <div
                        key={item.id || idx}
                        style={{
                          padding: "14px 16px",
                          backgroundColor: itemBg,
                          borderRadius: 10,
                          border: `1px solid ${itemStatusColor}30`,
                          borderLeft: `4px solid ${itemStatusColor}`,
                          transition: "all 0.2s",
                        }}
                      >
                        {/* Item Header */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 200 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                              <div className="admin-avatar small">
                                <span style={{ fontSize: 10 }}>{(item.businessName || item.name || "?").split(" ").map((n) => n[0]).join("").substring(0, 2)}</span>
                              </div>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{item.businessName || item.name}</div>
                                <div style={{ fontSize: 11, color: "#6b7280", display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                                  <span style={{ color: "#2563eb", fontWeight: 700, fontFamily: "monospace" }}>{item.leadId || "—"}</span>
                                  <span>• {item.contactNumber || item.phone || "—"}</span>
                                  <span className="status-badge badge-source" style={{ fontSize: 9, padding: "2px 6px" }}>{item.category || "—"}</span>
                                </div>
                              </div>
                            </div>

                            {/* Employee Data */}
                            {(item.callDate || item.decisionMaker || item.requirement || item.painPoint) && (
                              <div style={{
                                marginTop: 8,
                                padding: 10,
                                backgroundColor: "white",
                                borderRadius: 8,
                                border: "1px solid #e5e7eb",
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                                gap: 8,
                                fontSize: 12
                              }}>
                                {item.callDate && (
                                  <div><span style={{ color: "#9ca3af" }}>📞 Call: </span><strong>{item.callDate}</strong></div>
                                )}
                                {item.decisionMaker && (
                                  <div><span style={{ color: "#9ca3af" }}>👤 DM: </span><strong>{item.decisionMaker}</strong></div>
                                )}
                                {item.requirement && (
                                  <div><span style={{ color: "#9ca3af" }}>📋 Req: </span><strong>{item.requirement.substring(0, 40)}{item.requirement.length > 40 ? "..." : ""}</strong></div>
                                )}
                                {item.painPoint && (
                                  <div><span style={{ color: "#9ca3af" }}>⚠️ Pain: </span><strong>{item.painPoint.substring(0, 40)}{item.painPoint.length > 40 ? "..." : ""}</strong></div>
                                )}
                                {item.employeeNotes && (
                                  <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "#9ca3af" }}>📝 Notes: </span><strong>{item.employeeNotes}</strong></div>
                                )}
                                {item.completedAt && (
                                  <div><span style={{ color: "#16a34a" }}>✅ Done: </span><strong>{new Date(item.completedAt).toLocaleDateString("en-IN")}</strong></div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Right Side: Status + Actions */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0 }}>
                            <span className={`status-badge badge-${item.status}`} style={{ fontSize: 11, padding: "4px 12px" }}>
                              {item.status === "completed" ? "✅ Completed" : item.status === "in_progress" ? "🔄 In Progress" : "⏳ Pending"}
                            </span>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button
                                onClick={() => openItemEdit(selectedTask, item)}
                                disabled={modalLoading}
                                style={{
                                  padding: "4px 10px", borderRadius: 6, border: "1px solid #bfdbfe",
                                  background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 700, cursor: "pointer"
                                }}
                              >✏️ Edit</button>
                              {item.status !== "completed" && (
                                <button
                                  onClick={() => handleQuickStatus(selectedTask, item.id, "completed")}
                                  disabled={modalLoading}
                                  style={{
                                    padding: "4px 10px", borderRadius: 6, border: "1px solid #bbf7d0",
                                    background: "#f0fdf4", color: "#166534", fontSize: 11, fontWeight: 700, cursor: "pointer"
                                  }}
                                >✅ Done</button>
                              )}
                            </div>
                            {item.updatedBy && (
                              <span style={{ fontSize: 10, color: "#9ca3af" }}>
                                by {item.updatedBy}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* ========== NOTES SECTION ========== */}
            <div style={{ padding: "20px 28px", borderTop: "1px solid #f3f4f6", backgroundColor: "#f9fafb", borderRadius: "0 0 16px 16px" }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 10 }}>📝 Task Notes</h4>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text" value={noteInput} onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Add a note..." disabled={modalLoading}
                  style={{ flex: 1, padding: "10px 16px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                />
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={handleAddNote} disabled={modalLoading || !noteInput.trim()}>Add</button>
              </div>
              <div style={{ marginTop: 10, maxHeight: 120, overflowY: "auto" }}>
                {(selectedTask.notes || []).length === 0 ? (
                  <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", padding: 8 }}>No notes yet</p>
                ) : (
                  (selectedTask.notes || []).reverse().map((note) => (
                    <div key={note.id || note.timestamp} style={{
                      padding: "8px 12px", backgroundColor: "white", borderRadius: 8, marginBottom: 6,
                      fontSize: 12, border: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center"
                    }}>
                      <div>
                        <strong style={{ color: "#2563eb" }}>{note.userName}: </strong>
                        <span>{note.note}</span>
                      </div>
                      <span style={{ color: "#9ca3af", fontSize: 10, flexShrink: 0, marginLeft: 12 }}>
                        {new Date(note.timestamp).toLocaleString("en-IN")}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ========== FOOTER ========== */}
            <div style={{ padding: "16px 28px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: "white" }}>
              <button className="btn btn-outline" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== ITEM EDIT MODAL ========== */}
      {showItemEditModal && selectedTask && selectedItem && (
        <div className="modal-overlay" onClick={() => setShowItemEditModal(false)}>
          <div className="modal-box modal-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>✏️ Fill Data</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#6b7280" }}>{selectedItem.businessName || selectedItem.name} • {selectedItem.leadId || ""}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowItemEditModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {modalError && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}><p style={{ color: "#991b1b", fontSize: 14, margin: 0 }}>❌ {modalError}</p></div>}
              {modalSuccess && <div style={{ marginBottom: 12, padding: 12, backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}><p style={{ color: "#166534", fontSize: 14, margin: 0 }}>{modalSuccess}</p></div>}

              <div style={{ padding: 12, backgroundColor: "#f9fafb", borderRadius: 8, marginBottom: 16, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#6b7280" }}>Lead ID</span><strong style={{ color: "#2563eb" }}>{selectedItem.leadId || "—"}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#6b7280" }}>Business</span><strong>{selectedItem.businessName || selectedItem.name}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#6b7280" }}>Category</span><strong>{selectedItem.category || "—"}</strong></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}><span style={{ color: "#6b7280" }}>Contact</span><strong>{selectedItem.contactNumber || selectedItem.phone || "—"}</strong></div>
              </div>

              <div className="admin-form-group" style={{ marginBottom: 14 }}><label>📞 Call Date *</label><input type="date" name="callDate" value={itemEditForm.callDate} onChange={handleItemEditChange} disabled={modalLoading} /></div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}><label>👤 Decision Maker</label><input type="text" name="decisionMaker" value={itemEditForm.decisionMaker} onChange={handleItemEditChange} placeholder="e.g. Mr. Sharma - CEO" disabled={modalLoading} /></div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}><label>📋 Requirement</label><textarea name="requirement" value={itemEditForm.requirement} onChange={handleItemEditChange} placeholder="What do they need?" rows="2" disabled={modalLoading} /></div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}><label>⚠️ Pain Point</label><textarea name="painPoint" value={itemEditForm.painPoint} onChange={handleItemEditChange} placeholder="What's their problem?" rows="2" disabled={modalLoading} /></div>
              <div className="admin-form-group" style={{ marginBottom: 14 }}><label>📝 Notes</label><textarea name="employeeNotes" value={itemEditForm.employeeNotes} onChange={handleItemEditChange} placeholder="Discussion summary..." rows="2" disabled={modalLoading} /></div>
              <div className="admin-form-group"><label>Status *</label>
                <div style={{ display: "flex", gap: 8 }}>
                  {[{ v: "pending", l: "⏳ Pending", c: "#6b7280" }, { v: "in_progress", l: "🔄 In Progress", c: "#f97316" }, { v: "completed", l: "✅ Completed", c: "#22c55e" }].map((opt) => (
                    <button key={opt.v} onClick={() => setItemEditForm((p) => ({ ...p, status: opt.v }))} disabled={modalLoading}
                      style={{
                        padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
                        border: itemEditForm.status === opt.v ? `2px solid ${opt.c}` : "1px solid #e5e7eb",
                        backgroundColor: itemEditForm.status === opt.v ? `${opt.c}15` : "white",
                        color: itemEditForm.status === opt.v ? opt.c : "#6b7280",
                      }}
                    >{opt.l}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowItemEditModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveItemEdit} disabled={modalLoading}>{modalLoading ? "⏳ Saving..." : "💾 Save Data"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ========== DELETE MODAL ========== */}
      {showDeleteModal && taskToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-box modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header" style={{ borderBottom: "1px solid #fecaca" }}><h3 style={{ color: "#dc2626" }}>🗑️ Delete Task</h3><button className="modal-close-btn" onClick={() => setShowDeleteModal(false)}>✕</button></div>
            <div className="modal-body" style={{ textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
              <h4 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>Are you sure?</h4>
              <p style={{ fontSize: 14, color: "#6b7280" }}><strong style={{ color: "#dc2626" }}>"{taskToDelete.title}"</strong> delete hoga. {(taskToDelete.assignedItems || []).length} items bhi delete ho jayenge.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowDeleteModal(false)} disabled={modalLoading}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteTask} disabled={modalLoading}>{modalLoading ? "⏳" : "🗑️ Yes, Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;